import { useEffect, useMemo, useState } from "react";
import { Product } from "../../types";
import { productService } from "../../services/productService";
import { FaqEntry } from "../../services/answerDeskService";
import { needsProduct } from "./answerDeskUtils";

// Live product data for answers written for specific products, so
// {name}/{price}/{stock}/{link} fill in even when nobody added the product
// to the chat. Cached briefly so prices stay current without refetching
// on every render.

const TTL_MS = 2 * 60 * 1000;
const cache = new Map<string, { product: Product | null; at: number }>();
const inflight = new Map<string, Promise<Product | null>>();

function fresh(id: string): Product | null | undefined {
    const hit = cache.get(id);
    if (!hit || Date.now() - hit.at > TTL_MS) return undefined;
    return hit.product;
}

export function loadProduct(id: string): Promise<Product | null> {
    const cached = fresh(id);
    if (cached !== undefined) return Promise.resolve(cached);
    const pending = inflight.get(id);
    if (pending) return pending;
    const request = productService
        .getProductById(id)
        .then((p) => p)
        .catch(() => null) // deleted product, or no access — leave it unfilled
        .then((product) => {
            cache.set(id, { product, at: Date.now() });
            inflight.delete(id);
            return product;
        });
    inflight.set(id, request);
    return request;
}

/** Product ids an entry was written for (product rules only). */
export const ownProductIds = (entry: FaqEntry): string[] =>
    entry.targets.map((t) => t.product_id).filter((id): id is string => !!id);

/**
 * Loads the products that the given answers were written for — only for
 * answers that actually contain product placeholders.
 */
export function useTargetProducts(entries: FaqEntry[]): Map<string, Product> {
    const ids = useMemo(() => {
        const set = new Set<string>();
        entries.forEach((e) => {
            if (needsProduct(e.answer)) ownProductIds(e).forEach((id) => set.add(id));
        });
        return Array.from(set).sort();
    }, [entries]);
    const key = ids.join(",");

    const [products, setProducts] = useState<Map<string, Product>>(() => new Map());

    useEffect(() => {
        if (!key) return;
        let cancelled = false;
        const wanted = key.split(",");
        // A few at a time so a long list never floods the API.
        const run = async () => {
            const found: Product[] = [];
            for (let i = 0; i < wanted.length; i += 4) {
                const batch = await Promise.all(wanted.slice(i, i + 4).map(loadProduct));
                batch.forEach((p) => p && found.push(p));
                if (cancelled) return;
                setProducts((prev) => {
                    const next = new Map(prev);
                    found.forEach((p) => next.set(p.product_id, p));
                    return next;
                });
            }
        };
        run();
        return () => {
            cancelled = true;
        };
    }, [key]);

    return products;
}

/**
 * Which products an answer's placeholders can be filled from, best first:
 * products in the chat it was written for → the products it was written
 * for (even if not in the chat) → any product in the chat.
 */
export function resolveFillProducts(
    entry: FaqEntry,
    chatProducts: Product[],
    matchIndex: Map<string, Map<string, number>>,
    targetProducts: Map<string, Product>,
): Product[] {
    if (entry.is_general) return chatProducts;
    const matched = chatProducts.filter((p) => matchIndex.get(p.product_id)?.has(entry.faq_entry_id));
    if (matched.length > 0) return matched;
    const own = ownProductIds(entry)
        .map((id) => targetProducts.get(id))
        .filter((p): p is Product => !!p);
    if (own.length > 0) return own;
    return chatProducts;
}
